/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ManagedDeviceRollback,
  ManagedDeviceRollback$inboundSchema,
} from "./manageddevicerollback.js";
import {
  ManagedDeviceTool,
  ManagedDeviceTool$inboundSchema,
} from "./manageddevicetool.js";

/**
 * device when this machine's own agent sent the report, matched by serial. user when the assigned user's agent sent it, which may run on another machine.
 */
export const ManagedDeviceAgentStateAttestation = {
  Device: "device",
  User: "user",
} as const;
/**
 * device when this machine's own agent sent the report, matched by serial. user when the assigned user's agent sent it, which may run on another machine.
 */
export type ManagedDeviceAgentStateAttestation = OpenEnum<
  typeof ManagedDeviceAgentStateAttestation
>;

/**
 * What a device's agent last reported it enforces. Carries no error text or user-facing message.
 */
export type ManagedDeviceAgentState = {
  /**
   * When the agent first applied that etag.
   */
  appliedAt?: Date | undefined;
  /**
   * Etag of the policy the agent last applied.
   */
  appliedEtag?: string | undefined;
  /**
   * CPU architecture, e.g. arm64.
   */
  arch?: string | undefined;
  /**
   * device when this machine's own agent sent the report, matched by serial. user when the assigned user's agent sent it, which may run on another machine.
   */
  attestation: ManagedDeviceAgentStateAttestation;
  /**
   * Resolved update mode: automatic, notify, or disabled.
   */
  autoUpdate?: string | undefined;
  /**
   * True when the agent build is in blocked_versions.
   */
  currentBlocked: boolean;
  /**
   * Start of the window tools[].drift_corrections counts over. Omitted when no tool measures drift.
   */
  driftCountingSince?: Date | undefined;
  /**
   * True when the kill switch has halted policy enforcement.
   */
  enforcementHalted: boolean;
  /**
   * A self-rollback: the updated build failed to start, so the agent restored the previous one.
   */
  lastRollback?: ManagedDeviceRollback | undefined;
  /**
   * Operating system, e.g. darwin.
   */
  os?: string | undefined;
  /**
   * Version the organization pinned the fleet to, when set.
   */
  pinnedTarget?: string | undefined;
  /**
   * When the control plane received the report.
   */
  reportedAt: Date;
  /**
   * Version of the menu bar app.
   */
  speakeasyUiVersion?: string | undefined;
  /**
   * Version of the speakeasy CLI.
   */
  speakeasyVersion?: string | undefined;
  /**
   * Version of the speakeasyd daemon.
   */
  speakeasydVersion?: string | undefined;
  /**
   * Each managed tool's outcome from the agent's last reconcile.
   */
  tools: Array<ManagedDeviceTool>;
  /**
   * Update channel the agent follows, e.g. stable.
   */
  updateChannel?: string | undefined;
};

/** @internal */
export const ManagedDeviceAgentStateAttestation$inboundSchema: z.ZodMiniType<
  ManagedDeviceAgentStateAttestation,
  unknown
> = openEnums.inboundSchema(ManagedDeviceAgentStateAttestation);

/** @internal */
export const ManagedDeviceAgentState$inboundSchema: z.ZodMiniType<
  ManagedDeviceAgentState,
  unknown
> = z.pipe(
  z.object({
    applied_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    applied_etag: z.optional(z.string()),
    arch: z.optional(z.string()),
    attestation: ManagedDeviceAgentStateAttestation$inboundSchema,
    auto_update: z.optional(z.string()),
    current_blocked: z.boolean(),
    drift_counting_since: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    enforcement_halted: z.boolean(),
    last_rollback: z.optional(ManagedDeviceRollback$inboundSchema),
    os: z.optional(z.string()),
    pinned_target: z.optional(z.string()),
    reported_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    speakeasy_ui_version: z.optional(z.string()),
    speakeasy_version: z.optional(z.string()),
    speakeasyd_version: z.optional(z.string()),
    tools: z.array(ManagedDeviceTool$inboundSchema),
    update_channel: z.optional(z.string()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "applied_at": "appliedAt",
      "applied_etag": "appliedEtag",
      "auto_update": "autoUpdate",
      "current_blocked": "currentBlocked",
      "drift_counting_since": "driftCountingSince",
      "enforcement_halted": "enforcementHalted",
      "last_rollback": "lastRollback",
      "pinned_target": "pinnedTarget",
      "reported_at": "reportedAt",
      "speakeasy_ui_version": "speakeasyUiVersion",
      "speakeasy_version": "speakeasyVersion",
      "speakeasyd_version": "speakeasydVersion",
      "update_channel": "updateChannel",
    });
  }),
);

export function managedDeviceAgentStateFromJSON(
  jsonString: string,
): SafeParseResult<ManagedDeviceAgentState, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => ManagedDeviceAgentState$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'ManagedDeviceAgentState' from JSON`,
  );
}
